/**
 * NLWorkflowPreview - Preview parsed NL workflow steps (T7.2)
 *
 * Shows parsed steps as cards with matched agent, confidence score,
 * and manual override dropdown for unmatched steps.
 * Confirm/Edit/Cancel actions.
 *
 * Patent: Natural Language Workflow Builder for AI-Governed Systems
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import React, { useState } from 'react';
import { FiCheck, FiX, FiAlertTriangle, FiChevronDown } from 'react-icons/fi';
import type { ParsedWorkflow, ParsedStep } from './types';

interface NLWorkflowPreviewProps {
  workflow: ParsedWorkflow;
  confidence: number;
  availableAgents: Array<{
    id: string;
    name: string;
    category: string;
  }>;
  onConfirm: (steps: ParsedStep[]) => void;
  onCancel: () => void;
}

const confidenceColor = (c: number): string => {
  if (c >= 0.7) return 'text-green-700 bg-green-100';
  if (c >= 0.4) return 'text-amber-700 bg-amber-100';
  return 'text-red-700 bg-red-100';
};

const complexityColor: Record<string, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-red-100 text-red-700',
};

const NLWorkflowPreview: React.FC<NLWorkflowPreviewProps> = ({
  workflow,
  confidence,
  availableAgents,
  onConfirm,
  onCancel,
}) => {
  const [steps, setSteps] = useState<ParsedStep[]>(workflow.steps);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const handleAgentOverride = (stepOrder: number, agentId: string) => {
    const agent = availableAgents.find(a => a.id === agentId);
    if (!agent) return;

    setSteps(prev => prev.map(s =>
      s.order === stepOrder
        ? { ...s, matchedAgentId: agent.id, matchedAgentName: agent.name, confidence: 1.0 }
        : s
    ));
  };

  const handleRemoveStep = (stepOrder: number) => {
    setSteps(prev => prev.filter(s => s.order !== stepOrder));
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Generated Workflow Preview</h4>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceColor(confidence)}`}>
              {(confidence * 100).toFixed(0)}% confidence
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${complexityColor[workflow.estimatedComplexity]}`}>
              {workflow.estimatedComplexity} complexity
            </span>
            <span className="text-xs text-gray-500">{steps.length} steps</span>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {workflow.warnings && workflow.warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          {workflow.warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-amber-700">
              <FiAlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Steps */}
      <div className="space-y-2">
        {steps.map((step) => (
          <div
            key={step.order}
            className={`border rounded-lg p-3 transition-colors ${
              step.matchedAgentId ? 'border-gray-200 bg-white' : 'border-amber-300 bg-amber-50'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center mt-0.5">
                  {step.order}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{step.description}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    {step.matchedAgentId ? (
                      <span className="inline-flex items-center px-2 py-0.5 bg-indigo-100 text-indigo-800 text-xs rounded-full font-medium">
                        {step.matchedAgentName}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                        No agent matched
                      </span>
                    )}
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono ${confidenceColor(step.confidence)}`}>
                      {(step.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1 ml-2">
                {/* Override dropdown toggle */}
                <button
                  onClick={() => setExpandedStep(expandedStep === step.order ? null : step.order)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  title="Change agent"
                >
                  <FiChevronDown className={`h-4 w-4 transition-transform ${expandedStep === step.order ? 'rotate-180' : ''}`} />
                </button>
                <button
                  onClick={() => handleRemoveStep(step.order)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                  title="Remove step"
                >
                  <FiX className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Agent override dropdown */}
            {expandedStep === step.order && (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">Assign Agent</label>
                <select
                  value={step.matchedAgentId || ''}
                  onChange={(e) => handleAgentOverride(step.order, e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">-- Select Agent --</option>
                  {availableAgents.map(agent => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.category})
                    </option>
                  ))}
                </select>

                {/* Suggested alternatives */}
                {step.suggestedAlternatives && step.suggestedAlternatives.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Suggestions:</p>
                    <div className="flex flex-wrap gap-1">
                      {step.suggestedAlternatives.map(alt => (
                        <button
                          key={alt.agentId}
                          onClick={() => handleAgentOverride(step.order, alt.agentId)}
                          className="inline-flex items-center px-2 py-0.5 bg-gray-100 hover:bg-indigo-100 text-xs rounded-full text-gray-700 hover:text-indigo-700 transition-colors"
                        >
                          {alt.agentName} ({(alt.confidence * 100).toFixed(0)}%)
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(steps)}
          disabled={steps.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          <FiCheck className="h-4 w-4" />
          Add {steps.length} Steps to Workflow
        </button>
      </div>
    </div>
  );
};

export default NLWorkflowPreview;
